import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { RootState } from './index';
import { DeliveryMethodTypes, PromotionPackageCustomerContactEntity, PromotionPackageEntity, PromotionPackageInfoJson } from '../types/promotion';

export type LangKeys = 'he' | 'en' | 'ru' | 'ar';
export type ApiEnv = 'DEV' | 'STAGE' | 'PROD';
export type ErrSource = 'validatePackage' | 'sendCode' | 'verifyCode' | 'createOffer' | null;
export type A11yContrast = 'normal' | 'high' | 'blackAndWhite';

const A11Y_STORAGE_KEY = 'a11y';

interface A11ySettings {
    contrast: A11yContrast;
    linkHighlight: boolean;
}

const DEFAULT_A11Y: A11ySettings = { contrast: 'normal', linkHighlight: false };

const readStoredA11y = (): A11ySettings => {
    try {
        const raw = typeof window !== 'undefined' ? window.localStorage.getItem(A11Y_STORAGE_KEY) : null;
        if (!raw) return DEFAULT_A11Y;
        const parsed = JSON.parse(raw);
        return {
            contrast: parsed.contrast === 'high' || parsed.contrast === 'blackAndWhite' ? parsed.contrast : 'normal',
            linkHighlight: !!parsed.linkHighlight,
        };
    } catch {
        return DEFAULT_A11Y;
    }
};

export interface SentOffer {
    method: DeliveryMethodTypes;
    addressingId: string;
}

export interface MainState {
    language: LangKeys;
    isLoading: boolean;
    isFail: boolean;
    errCode: number;
    errSource: ErrSource;
    flowToken: string;
    packageToken: string;
    /** Raw query string (e.g. "token=abc&ENV=DEV") the flow was originally opened with. */
    originalQuery: string;
    env: ApiEnv;
    phone: string;
    packageInfo: PromotionPackageInfoJson | null;
    promotionPackage: PromotionPackageEntity | null;
    customersContacts: PromotionPackageCustomerContactEntity[];
    selectedContactToken: string;
    sentOffer: SentOffer | null;
    codeSentAt: number | null;
    codeValidMinutes: number;
    numberOfTriesLeft: number;
    a11yContrast: A11yContrast;
    a11yLinkHighlight: boolean;
}

const initialState: MainState = {
    language: 'he',
    isLoading: false,
    isFail: false,
    errCode: 0,
    errSource: null,
    flowToken: '',
    packageToken: '',
    originalQuery: '',
    env: 'PROD',
    phone: '',
    packageInfo: null,
    promotionPackage: null,
    customersContacts: [],
    selectedContactToken: '',
    sentOffer: null,
    codeSentAt: null,
    codeValidMinutes: 5,
    numberOfTriesLeft: 5,
    a11yContrast: readStoredA11y().contrast,
    a11yLinkHighlight: readStoredA11y().linkHighlight,
}

export const mainSlice = createSlice({
    name: 'main',
    initialState,
    reducers: {
        setLanguage(state, action: PayloadAction<LangKeys>) {
            state.language = action.payload;
        },
        setIsLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        setIsFail(state, action: PayloadAction<boolean>) {
            state.isFail = action.payload;
        },
        setErrCode(state, action: PayloadAction<number>) {
            state.errCode = action.payload;
        },
        setErrSource(state, action: PayloadAction<ErrSource>) {
            state.errSource = action.payload;
        },
        setFlowToken(state, action: PayloadAction<string>) {
            state.flowToken = action.payload;
        },
        setPackageToken(state, action: PayloadAction<string>) {
            state.packageToken = action.payload;
        },
        setOriginalQuery(state, action: PayloadAction<string>) {
            state.originalQuery = action.payload;
        },
        setEnv(state, action: PayloadAction<ApiEnv>) {
            state.env = action.payload;
        },
        setPhone(state, action: PayloadAction<string>) {
            state.phone = action.payload;
        },
        setPackageInfo(state, action: PayloadAction<PromotionPackageInfoJson | null>) {
            state.packageInfo = action.payload;
        },
        setPromotionPackage(state, action: PayloadAction<PromotionPackageEntity | null>) {
            state.promotionPackage = action.payload;
        },
        setCustomersContacts(state, action: PayloadAction<PromotionPackageCustomerContactEntity[]>) {
            state.customersContacts = action.payload;
        },
        setSelectedContactToken(state, action: PayloadAction<string>) {
            state.selectedContactToken = action.payload;
        },
        setSentOffer(state, action: PayloadAction<SentOffer | null>) {
            state.sentOffer = action.payload;
        },
        setCodeSentAt(state, action: PayloadAction<number | null>) {
            state.codeSentAt = action.payload;
        },
        setCodeValidMinutes(state, action: PayloadAction<number>) {
            state.codeValidMinutes = action.payload;
        },
        setNumberOfTriesLeft(state, action: PayloadAction<number>) {
            state.numberOfTriesLeft = action.payload;
        },
        setA11yContrast(state, action: PayloadAction<A11yContrast>) {
            state.a11yContrast = action.payload;
            persistA11y(state);
        },
        setA11yLinkHighlight(state, action: PayloadAction<boolean>) {
            state.a11yLinkHighlight = action.payload;
            persistA11y(state);
        },
        resetA11y(state) {
            state.a11yContrast = DEFAULT_A11Y.contrast;
            state.a11yLinkHighlight = DEFAULT_A11Y.linkHighlight;
            persistA11y(state);
        },
    },
  })

  function persistA11y(state: MainState) {
    try {
        window.localStorage.setItem(
            A11Y_STORAGE_KEY,
            JSON.stringify({ contrast: state.a11yContrast, linkHighlight: state.a11yLinkHighlight }),
        );
    } catch {
        // localStorage unavailable (private mode, disabled, etc.) — a11y prefs just won't persist.
    }
  }

  export const {
    setLanguage,
    setIsLoading,
    setIsFail,
    setErrCode,
    setErrSource,
    setFlowToken,
    setPackageToken,
    setOriginalQuery,
    setEnv,
    setPhone,
    setPackageInfo,
    setPromotionPackage,
    setCustomersContacts,
    setSelectedContactToken,
    setSentOffer,
    setCodeSentAt,
    setCodeValidMinutes,
    setNumberOfTriesLeft,
    setA11yContrast,
    setA11yLinkHighlight,
    resetA11y,
  } = mainSlice.actions;

  export const mainSliceSelector = (state: RootState): MainState => state.main;
export default mainSlice.reducer
